import React, { useEffect, useState } from 'react';
import { CheckCircle, Loader2, XCircle, ArrowRight, Clock, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PaymentSuccessPage = ({ onContinue }) => {
  const [status, setStatus] = useState('loading'); // loading, success, pending, error
  const [planName, setPlanName] = useState('');
  const [amount, setAmount] = useState(0);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const checkPayment = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (!sessionId) {
      setStatus('error');
      setError('No se encontró la sesión de pago');
      return;
    }

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPlanName(data.plan_id === 'basic' ? 'Básico' : 'Premium');
        setAmount(data.amount || 0);
        
        // Update local user data with new plan
        const userData = JSON.parse(localStorage.getItem('user_data') || '{}');
        userData.plan = data.plan_id;
        userData.plan_details = {
          name: data.plan_id === 'basic' ? 'Básico' : 'Premium',
          max_assets: data.plan_id === 'basic' ? 20 : -1,
          has_alerts: true,
          has_indicators: true,
          has_export: true,
          has_backtest: data.plan_id === 'premium'
        };
        localStorage.setItem('user_data', JSON.stringify(userData));
      } else if (data.payment_status === 'unpaid' || data.status === 'open') {
        setStatus('pending');
        setPlanName(data.plan_id === 'basic' ? 'Básico' : 'Premium');
      } else {
        setStatus('error');
        setError('El pago no se completó correctamente');
      }
    } catch (err) {
      setStatus('error');
      setError(err.message);
    }
  };

  useEffect(() => {
    checkPayment();
  }, []);

  // Auto-retry para pagos pendientes
  useEffect(() => {
    if (status === 'pending' && retryCount < 5) {
      const timer = setTimeout(() => {
        setRetryCount(prev => prev + 1);
        checkPayment();
      }, 3000); // Reintentar cada 3 segundos
      return () => clearTimeout(timer);
    }
  }, [status, retryCount]);

  const handleRetry = () => {
    setStatus('loading');
    setRetryCount(0);
    checkPayment();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        {status === 'loading' && (
          <div className="bg-slate-900/80 backdrop-blur-lg border border-slate-700 rounded-2xl p-12">
            <Loader2 size={64} className="text-gold-500 animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-white mb-2">Verificando pago...</h2>
            <p className="text-slate-400">Por favor espera un momento</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-slate-900/80 backdrop-blur-lg border border-green-500/30 rounded-2xl p-12">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={48} className="text-green-500" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">¡Pago Exitoso!</h2>
            <p className="text-slate-400 mb-2">
              Has activado el plan <span className="text-gold-400 font-semibold">{planName}</span>
            </p>
            {amount > 0 && (
              <p className="text-green-400 text-lg font-mono mb-4">{amount.toFixed(2)}€</p>
            )}
            <p className="text-slate-500 text-sm mb-8">
              Ya puedes disfrutar de todas las funciones de tu nuevo plan.
            </p>
            <button
              onClick={onContinue}
              className="w-full py-3 px-6 bg-gradient-to-r from-gold-500 to-zinc-600 hover:from-gold-400 hover:to-zinc-500 text-white rounded-lg font-semibold text-lg transition-all shadow-lg shadow-gold-500/25 flex items-center justify-center gap-2"
            >
              Ir a mi Dashboard
              <ArrowRight size={20} />
            </button>
          </div>
        )}

        {status === 'pending' && (
          <div className="bg-slate-900/80 backdrop-blur-lg border border-gold-500/30 rounded-2xl p-12">
            <div className="w-20 h-20 bg-gold-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock size={48} className="text-gold-500 animate-pulse" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Procesando Pago...</h2>
            <p className="text-slate-400 mb-4">
              Tu pago para el plan <span className="text-gold-400 font-semibold">{planName}</span> está siendo procesado.
            </p>
            <p className="text-slate-500 text-sm mb-6">
              Esto puede tardar unos segundos. {retryCount > 0 && `Verificando... (${retryCount}/5)`}
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleRetry}
                className="flex-1 py-3 px-6 bg-gold-600 hover:bg-gold-500 text-white rounded-lg font-semibold transition-all flex items-center justify-center gap-2"
              >
                <RefreshCw size={18} />
                Verificar de nuevo
              </button>
              <button
                onClick={onContinue}
                className="flex-1 py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-all"
              >
                Volver
              </button>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-slate-900/80 backdrop-blur-lg border border-red-500/30 rounded-2xl p-12">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle size={48} className="text-red-500" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Error en el Pago</h2>
            <p className="text-slate-400 mb-6">{error || 'Hubo un problema con tu pago'}</p>
            <div className="flex gap-3">
              <button
                onClick={handleRetry}
                className="flex-1 py-3 px-6 bg-slate-600 hover:bg-slate-500 text-white rounded-lg font-semibold transition-all flex items-center justify-center gap-2"
              >
                <RefreshCw size={18} />
                Reintentar
              </button>
              <button
                onClick={onContinue}
                className="flex-1 py-3 px-6 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-all"
              >
                Volver al Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
